'use client';

import type { customerJWT, loginResponse } from '@repo/customer-api';
import { DevCasePicker, DevOptionSelect, DevPanel as DevPanelShell, DevToggleRow } from '@repo/ui';
import { deleteClientCookie, getClientCookie, parseJwt, setClientCookie } from '@repo/utils';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { CREDIT_CASES, CREDIT_PRESETS, DEFAULT_MOCK_CONFIG, OFFERS_SCENARIOS, parseMockConfig } from './config';

/**
 * Customer dev tools: on top of the shared panel, picks which operations the mock customer has (alone or from a
 * preset), global mora and the offers in the carousel.
 */
export const DevPanel = () => {
  const router = useRouter();
  const [offers, setOffers] = useState('none');

  return (
    <DevPanelShell
      envForcesMock={process.env.NEXT_PUBLIC_CUSTOMER_API_FORCEMOCK === 'true'}
      envFlagName="NEXT_PUBLIC_CUSTOMER_API_FORCEMOCK"
      defaults={DEFAULT_MOCK_CONFIG}
      parse={parseMockConfig}
      failureHelper="Aplica a operaciones, detalle, perfil y documentos."
      renderScenarios={({ config, update, disabled }) => (
        <>
          <DevCasePicker
            title="Operaciones del cliente"
            cases={CREDIT_CASES}
            presets={CREDIT_PRESETS}
            selected={config.cases}
            onChange={(cases) => update({ cases })}
            onPreset={(preset) => update({ cases: preset.cases, mora: !!preset.mora })}
            isPresetActive={(preset) => !!preset.mora === config.mora}
            disabled={disabled}
          />
          <DevToggleRow
            id="dev-tools-mora"
            label="Bloqueado por mora"
            hint="Mora global: Operaciones y Pagos muestran solo el aviso de mora."
            checked={config.mora}
            disabled={disabled}
            onChange={(mora) => update({ mora })}
          />
        </>
      )}
      extras={
        <DevOptionSelect
          label="Ofertas"
          value={offers}
          options={OFFERS_SCENARIOS}
          onChange={setOffers}
          helper="El carrusel lee la cookie de ofertas, también sin mock."
        />
      }
      // The offers carousel reads the "offers" cookie the login used to write, so the panel writes it directly
      onOpen={() => {
        const offersCookie = decodeURIComponent(getClientCookie('offers') || '[]');
        setOffers(OFFERS_SCENARIOS.find(({ ratings }) => JSON.stringify(ratings) === offersCookie)?.id ?? 'none');
      }}
      onSave={() => {
        const { ratings } = OFFERS_SCENARIOS.find(({ id }) => id === offers)!;
        if (ratings.length) setClientCookie('offers', encodeURIComponent(JSON.stringify(ratings)));
        else deleteClientCookie('offers');
      }}
      onReset={() => deleteClientCookie('offers')}
      // Signs in with the mock login's token, so a logged-out browser can reach the sections with mock data
      mockLogin={{
        tokenCookie: 'token-customer',
        login: async () => {
          const { token } = (await (await fetch('/mock/api/login', { method: 'POST' })).json()) as loginResponse;
          const { exp, userId } = parseJwt(token) as customerJWT;
          setClientCookie('token-customer', token, new Date(exp * 1000).toUTCString());
          setClientCookie('userId-customer', `${userId}`);
          router.push('/loans/operations');
        },
      }}
      // Phones: left, above the bottom tab bar. Desktop: right, as the menu drawer covers the left edge, and above the
      // Sendpulse chat bubble
      fabSx={{ left: { xs: 16, md: 'auto' }, right: { md: 24 }, bottom: { xs: 96, md: 104 } }}
    />
  );
};
